import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "plan-page" }
const _hoisted_2 = { class: "plan-shell" }
const _hoisted_3 = { class: "toolbar" }
const _hoisted_4 = { class: "patient-picker" }
const _hoisted_5 = {
  key: 0,
  class: "patient-results"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "summary-grid" }
const _hoisted_10 = { class: "content-grid" }
const _hoisted_11 = { class: "plan-list" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { class: "detail-panel" }
const _hoisted_15 = { class: "timeline" }
const _hoisted_16 = { class: "table-area" }
const _hoisted_17 = ["disabled"]

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

type Patient = { id?: number; chartNo: string; name: string; phone?: string };
type PlanItem = { id: number; tooth: string; name: string; price: number; memo: string };
type TreatmentPlan = { id: number; chartNo: string; patientName: string; date: string; title: string; items: PlanItem[]; subtotal: number; vat: number; total: number; doctor: string; counselor: string; memo: string; status: string; consultMemo?: string; consultStatus?: string };

const STORAGE_KEY = 'bioa-treatment-plans';

export default /*@__PURE__*/_defineComponent({
  __name: 'PlanPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const patients = ref<Patient[]>(loadPatients());
const plans = ref<TreatmentPlan[]>(readStorage<TreatmentPlan[]>(STORAGE_KEY, []));
const queryChartNo = String(route.query.chartNo || '');
const queryPlanId = Number(route.query.planId || 0);
const currentPatient = ref<Patient | null>(patients.value.find((p) => p.chartNo === queryChartNo) || patients.value[0] || null);
const patientQuery = ref(currentPatient.value ? `${currentPatient.value.name} ${currentPatient.value.chartNo}` : '');
const patientListOpen = ref(false);
const selectedPlanId = ref<number | null>(queryPlanId || null);
const consultMemo = ref('');

const filteredPatients = computed(() => {
  const q = patientQuery.value.trim().toLowerCase();
  return patients.value.filter((p) => [p.name, p.chartNo, p.phone].join(' ').toLowerCase().includes(q)).slice(0, 40);
});
const patientPlans = computed(() => plans.value.filter((plan) => !currentPatient.value || plan.chartNo === currentPatient.value.chartNo).sort((a, b) => b.id - a.id));
const selectedPlan = computed(() => patientPlans.value.find((plan) => plan.id === selectedPlanId.value) || patientPlans.value[0] || null);
const submittedCount = computed(() => patientPlans.value.filter((plan) => plan.status === '제출').length);
const timeline = computed(() => {
  const plan = selectedPlan.value;
  if (!plan) return [];
  return [
    { id: 'created', date: plan.date, title: '치료비 계획 작성', detail: `${plan.items.length}개 항목 · ${money(plan.total)}`, type: 'created' },
    { id: 'submitted', date: plan.date, title: `상태 ${plan.status}`, detail: plan.memo || '상담 대기', type: plan.status === '제출' ? 'submitted' : 'created' },
    { id: 'consult', date: today(), title: plan.consultStatus || '상담 진행', detail: plan.consultMemo || consultMemo.value || '상담 내용을 입력하세요.', type: 'consult' },
  ];
});

watch(selectedPlan, (plan) => {
  if (plan) {
    selectedPlanId.value = plan.id;
    consultMemo.value = plan.consultMemo || '';
  }
}, { immediate: true });

function selectPatient(patient: Patient) {
  currentPatient.value = patient;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
  selectedPlanId.value = null;
}

function goPricing() {
  router.push('/price');
}

function markConfirmed() {
  updateSelected({ status: '진행확정', consultStatus: '진행 확정' });
}

function markCompleted() {
  updateSelected({ consultStatus: '상담 완료' });
}

function saveMemo() {
  updateSelected({ consultMemo: consultMemo.value, consultStatus: '상담 기록' });
}

function updateSelected(patch: Partial<TreatmentPlan>) {
  const plan = selectedPlan.value;
  if (!plan) return;
  const index = plans.value.findIndex((item) => item.id === plan.id);
  if (index < 0) return;
  plans.value[index] = { ...plans.value[index], ...patch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans.value));
}

function money(value: number) {
  return `${Math.round(value || 0).toLocaleString('ko-KR')}원`;
}

function loadPatients(): Patient[] {
  const stored = readStorage<Patient[]>('bioa-patients', []);
  if (stored.length) return stored.map((p) => ({ chartNo: p.chartNo || `P-${p.id || 'TEMP'}`, name: p.name || '환자', phone: p.phone || '' }));
  return [];
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _cache[11] || (_cache[11] = _createElementVNode("nav", { class: "tabs" }, [
        _createElementVNode("button", { class: "active" }, "치료 계획 상담")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, [
          _cache[3] || (_cache[3] = _createElementVNode("span", null, "환자", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((patientQuery).value = $event)),
            placeholder: "이름/차트/전화 검색",
            onFocus: _cache[1] || (_cache[1] = ($event: any) => (patientListOpen.value = true))
          }, null, 544), [
            [_vModelText, patientQuery.value]
          ]),
          (patientListOpen.value && patientQuery.value.trim())
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPatients.value, (patient) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: patient.chartNo,
                    type: "button",
                    onClick: ($event: any) => (selectPatient(patient))
                  }, [
                    _createElementVNode("strong", null, _toDisplayString(patient.name), 1),
                    _createElementVNode("span", null, _toDisplayString(patient.chartNo) + " · " + _toDisplayString(patient.phone || '-'), 1)
                  ], 8, _hoisted_6))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("button", { onClick: goPricing }, "치료비 계획 작성"),
        _createElementVNode("button", {
          disabled: !selectedPlan.value,
          onClick: markConfirmed
        }, "진행 확정", 8, _hoisted_7),
        _createElementVNode("button", {
          disabled: !selectedPlan.value,
          onClick: markCompleted
        }, "상담 완료", 8, _hoisted_8)
      ]),
      _createElementVNode("section", _hoisted_9, [
        _createElementVNode("article", null, [
          _cache[4] || (_cache[4] = _createElementVNode("span", null, "환자", -1)),
          _createElementVNode("strong", null, _toDisplayString(currentPatient.value?.name || '미선택'), 1)
        ]),
        _createElementVNode("article", null, [
          _cache[5] || (_cache[5] = _createElementVNode("span", null, "치료비 계획", -1)),
          _createElementVNode("strong", null, _toDisplayString(patientPlans.value.length) + "건", 1)
        ]),
        _createElementVNode("article", null, [
          _cache[6] || (_cache[6] = _createElementVNode("span", null, "제출 계획", -1)),
          _createElementVNode("strong", null, _toDisplayString(submittedCount.value) + "건", 1)
        ]),
        _createElementVNode("article", null, [
          _cache[7] || (_cache[7] = _createElementVNode("span", null, "선택 총액", -1)),
          _createElementVNode("strong", null, _toDisplayString(money(selectedPlan.value?.total || 0)), 1)
        ])
      ]),
      _createElementVNode("section", _hoisted_10, [
        _createElementVNode("aside", _hoisted_11, [
          _cache[8] || (_cache[8] = _createElementVNode("header", null, "환자별 치료계획", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(patientPlans.value, (plan) => {
            return (_openBlock(), _createElementBlock("button", {
              key: plan.id,
              type: "button",
              class: _normalizeClass({ selected: selectedPlanId.value === plan.id }),
              onClick: ($event: any) => (selectedPlanId.value = plan.id)
            }, [
              _createElementVNode("strong", null, _toDisplayString(plan.title), 1),
              _createElementVNode("span", null, _toDisplayString(plan.date) + " · " + _toDisplayString(plan.status) + " · " + _toDisplayString(money(plan.total)), 1)
            ], 10, _hoisted_12))
          }), 128)),
          (!patientPlans.value.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_13, "가격 책정에서 제출된 계획이 없습니다."))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("section", _hoisted_14, [
          _createElementVNode("header", null, [
            _createElementVNode("strong", null, _toDisplayString(selectedPlan.value?.title || '치료계획 선택'), 1),
            _createElementVNode("span", null, _toDisplayString(selectedPlan.value?.doctor || '-') + " · " + _toDisplayString(selectedPlan.value?.counselor || '-'), 1)
          ]),
          _createElementVNode("div", _hoisted_15, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timeline.value, (event) => {
              return (_openBlock(), _createElementBlock("article", {
                key: event.id,
                class: _normalizeClass(event.type)
              }, [
                _createElementVNode("span", null, _toDisplayString(event.date), 1),
                _createElementVNode("strong", null, _toDisplayString(event.title), 1),
                _createElementVNode("p", null, _toDisplayString(event.detail), 1)
              ], 2))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("table", null, [
              _cache[9] || (_cache[9] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", null, "치식/부위"),
                  _createElementVNode("th", null, "치료계획"),
                  _createElementVNode("th", null, "금액"),
                  _createElementVNode("th", null, "메모")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedPlan.value?.items || [], (item) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: item.id
                  }, [
                    _createElementVNode("td", null, _toDisplayString(item.tooth || '-'), 1),
                    _createElementVNode("td", null, _toDisplayString(item.name), 1),
                    _createElementVNode("td", null, _toDisplayString(money(item.price)), 1),
                    _createElementVNode("td", null, _toDisplayString(item.memo || '-'), 1)
                  ]))
                }), 128))
              ])
            ])
          ]),
          _createElementVNode("footer", null, [
            _createElementVNode("label", null, [
              _cache[10] || (_cache[10] = _createElementVNode("span", null, "상담 메모", -1)),
              _withDirectives(_createElementVNode("textarea", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((consultMemo).value = $event)),
                rows: "4",
                placeholder: "상담 결과, 보류 사유, 다음 상담 예정 등을 기록"
              }, null, 512), [
                [_vModelText, consultMemo.value]
              ])
            ]),
            _createElementVNode("button", {
              disabled: !selectedPlan.value,
              onClick: saveMemo
            }, "메모 저장", 8, _hoisted_17)
          ])
        ])
      ])
    ])
  ]))
}
}

})